import {announce} from '@github-ui/aria-live'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect} from 'react'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {CommitState} from '../../../hooks/use-autosave'
import {useSingleSelectOptions} from '../../../hooks/use-single-select-options'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {AutosaveMessage} from '../autosave-message'
import {SingleSelectForm} from '../single-select/single-select-form'
import styles from '../single-select/single-select-options.module.css'

type MultiSelectOptionsProps = {
  column: ColumnModelForDataType<typeof MemexColumnDataType.MultiSelect>
  disabled?: boolean
}

/**
 * Field settings panel for a project-defined (generic) multi-select column. Shares the option-management
 * primitives (`useSingleSelectOptions`, `useUpdateColumnOptions`, `SingleSelectForm`) with the single-select
 * settings UI: options are stored under `settings.options` in the same shape regardless of data type.
 *
 * Multi-select intentionally omits the status / workflow / default-value controls since multi-select fields do
 * not have a "default" value and are not used as the source for status auto-assign workflows.
 */
export const MultiSelectOptions: React.FC<MultiSelectOptionsProps> = ({column, disabled = false}) => {
  const {commitState, errorMessage, options, onDrop, addOption, canRemoveOption, removeOption, updateOption} =
    useSingleSelectOptions({externalColumn: column})

  useEffect(() => {
    if (commitState === CommitState.Successful) {
      announce('Saved!')
    }
  }, [commitState])

  // TODO: rename single-select form to something else since it is now used for both data types
  return (
    <>
      <Heading as="h3" className={styles.OptionsHeading} aria-label={`${column.name} options`}>
        Options
      </Heading>
      <SingleSelectForm
        options={options}
        onAddOption={addOption}
        onUpdateOption={updateOption}
        onRemoveOption={removeOption}
        canRemoveOption={canRemoveOption}
        onDrop={onDrop}
        className={clsx(commitState === CommitState.Failed && styles.SingleSelectForm)}
        noDuplicatedOptions={false}
        disabled={disabled}
        enableNameLengthValidation={false}
        enableDescriptionLengthValidation={false}
        disallowCommasInName={false}
      />

      {!disabled && <AutosaveMessage commitState={commitState} errorMessage={errorMessage} />}
    </>
  )
}
